import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Award,
  Users,
  DollarSign,
  Target,
  BarChart3,
  Crown,
  Medal,
  Sparkles,
  Filter,
  RefreshCw,
  FileText,
  ShoppingCart,
  Percent,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MetricasVendedores = ({ userRole, soloRanking = false }) => {
  const [loading, setLoading] = useState(true);
  const [metricas, setMetricas] = useState(null);
  const [filtros, setFiltros] = useState({
    mes: new Date().getMonth() + 1,
    anio: new Date().getFullYear(),
    acumuladoAnual: false
  });
  const [showFilters, setShowFilters] = useState(false);
  const [vistaExpandida, setVistaExpandida] = useState(false);

  const esAdminGerencia = userRole === 'admin' || userRole === 'gerencia';

  const fetchMetricas = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {
        mes: filtros.mes,
        anio: filtros.anio,
        acumulado_anual: filtros.acumuladoAnual
      };
      
      const response = await axios.get(`${API_URL}/api/metricas/vendedores`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetricas(response.data);
    } catch (error) {
      console.error('Error cargando métricas:', error);
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    fetchMetricas();
  }, [fetchMetricas]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '---';
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const getVariacionIcon = (variacion) => {
    switch (variacion) {
      case 'subio':
        return <TrendingUp className="w-4 h-4 text-emerald-400" />;
      case 'bajo':
        return <TrendingDown className="w-4 h-4 text-red-400" />;
      case 'nuevo':
        return <Sparkles className="w-4 h-4 text-[#D4AF37]" />;
      default:
        return <Minus className="w-4 h-4 text-zinc-500" />;
    }
  };

  const getPosicionIcon = (posicion) => {
    switch (posicion) {
      case 1:
        return <Crown className="w-5 h-5 text-yellow-400" />;
      case 2:
        return <Medal className="w-5 h-5 text-zinc-300" />;
      case 3:
        return <Medal className="w-5 h-5 text-amber-600" />;
      default:
        return <span className="text-zinc-500 font-medium">{posicion}.</span>;
    }
  };

  const getBarColor = (index) => {
    const colors = ['#D4AF37', '#A8A8A8', '#CD7F32', '#6B7280', '#4B5563', '#374151'];
    return colors[index] || '#374151';
  };

  // Preparar datos para gráfica comparativa
  const chartData = metricas?.ranking?.slice(0, 6).map(v => ({
    name: v.nombre?.split(' ')[0] || 'N/A',
    cotizaciones: v.cotizaciones_creadas || 0,
    ventas: v.cotizaciones_vendidas || 0,
    fichas: v.fichas_creadas || 0
  })) || [];

  if (loading) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-[#D4AF37]" />
        </div>
      </div>
    );
  }

  if (!metricas || !metricas.ranking || metricas.ranking.length === 0) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-[#D4AF37]" />
          <h3 className="font-semibold text-white">Ranking de Vendedores</h3>
        </div>
        <div className="text-center py-8 text-zinc-500">
          <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No hay actividad registrada en este período</p>
          <p className="text-sm mt-1">Las métricas aparecerán cuando haya cotizaciones o fichas con vendedor asignado</p>
        </div>
      </div>
    );
  }

  const primerLugar = metricas.ranking[0];

  return (
    <div className="space-y-4">
      {/* Header con filtros */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="font-semibold text-white">Ranking Comercial</h3>
            <span className="text-xs px-2 py-0.5 bg-zinc-800 rounded text-zinc-400">
              {filtros.acumuladoAnual 
                ? `Acumulado ${filtros.anio}` 
                : `${meses[filtros.mes - 1]} ${filtros.anio}`}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                showFilters ? 'bg-[#D4AF37] text-black' : 'bg-zinc-800 hover:bg-zinc-700'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filtros
            </button>
            <button
              onClick={fetchMetricas}
              className="p-1.5 hover:bg-zinc-800 rounded-lg transition-colors"
              title="Actualizar"
            >
              <RefreshCw className="w-4 h-4 text-zinc-400" />
            </button>
          </div>
        </div>

        {/* Panel de filtros */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-zinc-800 flex flex-wrap gap-3">
            <select
              value={filtros.mes}
              onChange={(e) => setFiltros(prev => ({ ...prev, mes: parseInt(e.target.value), acumuladoAnual: false }))}
              className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-[#D4AF37] focus:outline-none"
              disabled={filtros.acumuladoAnual}
            >
              {meses.map((mes, idx) => (
                <option key={`mes-${mes}`} value={idx + 1}>{mes}</option>
              ))}
            </select>
            
            <select
              value={filtros.anio}
              onChange={(e) => setFiltros(prev => ({ ...prev, anio: parseInt(e.target.value) }))}
              className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded-lg text-sm focus:border-[#D4AF37] focus:outline-none"
            >
              {[2024, 2025, 2026].map(anio => (
                <option key={anio} value={anio}>{anio}</option>
              ))}
            </select>
            
            <button
              onClick={() => setFiltros(prev => ({ ...prev, acumuladoAnual: !prev.acumuladoAnual }))}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                filtros.acumuladoAnual 
                  ? 'bg-[#D4AF37] text-black' 
                  : 'bg-zinc-800 hover:bg-zinc-700'
              }`}
            >
              Acumulado Anual
            </button>
          </div>
        )}
      </div>

      {/* Totales Generales - Solo si no es soloRanking */}
      {!soloRanking && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
            <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
              <FileText className="w-3.5 h-3.5" />
              Fichas
            </div>
            <p className="text-lg font-bold text-white">{metricas.totales?.total_fichas || 0}</p>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
            <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
              <Target className="w-3.5 h-3.5" />
              Cotizaciones
            </div>
            <p className="text-lg font-bold text-white">{metricas.totales?.total_cotizaciones || 0}</p>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
            <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
              <ShoppingCart className="w-3.5 h-3.5" />
              Ventas
            </div>
            <p className="text-lg font-bold text-emerald-400">{metricas.totales?.total_ventas || 0}</p>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
            <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
              <Percent className="w-3.5 h-3.5" />
              Conversión
            </div>
            <p className="text-lg font-bold text-[#D4AF37]">{metricas.totales?.conversion_general || 0}%</p>
          </div>
          
          {esAdminGerencia && (
            <>
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
                <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                  <DollarSign className="w-3.5 h-3.5" />
                  Cotizado
                </div>
                <p className="text-sm font-bold text-white">{formatCurrency(metricas.totales?.total_valor_cotizado)}</p>
              </div>
              
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
                <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                  <DollarSign className="w-3.5 h-3.5" />
                  Vendido
                </div>
                <p className="text-sm font-bold text-emerald-400">{formatCurrency(metricas.totales?.total_valor_vendido)}</p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Vendedor #1 destacado - Solo si no es soloRanking */}
      {!soloRanking && primerLugar && primerLugar.cotizaciones_vendidas > 0 && (
        <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/30 rounded-xl p-5">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-14 h-14 rounded-full bg-[#D4AF37]/30 flex items-center justify-center">
              <Crown className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#D4AF37] uppercase tracking-wider">Vendedor #1</span>
                {getVariacionIcon(primerLugar.variacion)}
              </div>
              <h4 className="text-xl font-semibold text-white">{primerLugar.nombre}</h4>
              <p className="text-sm text-zinc-400">{primerLugar.id_vendedor}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-xs text-zinc-400">Fichas</p>
                <p className="text-lg font-bold text-white">{primerLugar.fichas_creadas}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-400">Cotizaciones</p>
                <p className="text-lg font-bold text-white">{primerLugar.cotizaciones_creadas}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-400">Ventas</p>
                <p className="text-lg font-bold text-emerald-400">{primerLugar.cotizaciones_vendidas}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-400">Conversión</p>
                <p className="text-lg font-bold text-[#D4AF37]">{primerLugar.conversion_cotizacion}%</p>
              </div>
            </div>
            {esAdminGerencia && (
              <div className="text-right">
                <p className="text-2xl font-bold text-[#D4AF37]">
                  {formatCurrency(primerLugar.valor_total_vendido)}
                </p>
                <p className="text-xs text-zinc-400">Valor vendido</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabla Ranking Completa */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <h4 className="font-medium text-white flex items-center gap-2">
            <Award className="w-4 h-4 text-[#D4AF37]" />
            Ranking Detallado
          </h4>
          <button
            onClick={() => setVistaExpandida(!vistaExpandida)}
            className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white transition-colors"
          >
            {vistaExpandida ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {vistaExpandida ? 'Contraer' : 'Expandir'}
          </button>
        </div>
        
        {/* Tabla scrolleable */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-700">
              <tr className="text-left text-xs text-zinc-100 uppercase tracking-wider">
                <th className="px-4 py-3 font-semibold">#</th>
                <th className="px-4 py-3 font-semibold">Vendedor</th>
                <th className="px-4 py-3 font-semibold text-center">Fichas</th>
                <th className="px-4 py-3 font-semibold text-center">Cotiz.</th>
                {esAdminGerencia && <th className="px-4 py-3 font-semibold text-right">$ Cotizado</th>}
                <th className="px-4 py-3 font-semibold text-center">Ventas</th>
                {esAdminGerencia && <th className="px-4 py-3 font-semibold text-right">$ Vendido</th>}
                <th className="px-4 py-3 font-semibold text-center">Conv. Cot.</th>
                {vistaExpandida && (
                  <>
                    <th className="px-4 py-3 font-semibold text-center">Clientes</th>
                    <th className="px-4 py-3 font-semibold text-center">Conv. Ficha</th>
                    {esAdminGerencia && <th className="px-4 py-3 font-semibold text-right">Ticket Prom.</th>}
                  </>
                )}
                <th className="px-4 py-3 font-semibold text-center">Var.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {metricas.ranking.map((vendedor, idx) => (
                <tr
                  key={vendedor.vendedor_id}
                  className={`${idx === 0 ? 'bg-[#D4AF37]/5' : 'hover:bg-zinc-800/50'} transition-colors`}
                >
                  <td className="px-4 py-3">
                    <div className="w-7 flex justify-center">
                      {getPosicionIcon(vendedor.posicion)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-white">{vendedor.nombre}</p>
                      <p className="text-xs text-zinc-500">{vendedor.id_vendedor}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-white">{vendedor.fichas_creadas}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-white">{vendedor.cotizaciones_creadas}</span>
                  </td>
                  {esAdminGerencia && (
                    <td className="px-4 py-3 text-right">
                      <span className="text-zinc-300">{formatCurrency(vendedor.valor_total_cotizado)}</span>
                    </td>
                  )}
                  <td className="px-4 py-3 text-center">
                    <span className={`font-semibold ${vendedor.cotizaciones_vendidas > 0 ? 'text-emerald-400' : 'text-zinc-500'}`}>
                      {vendedor.cotizaciones_vendidas}
                    </span>
                  </td>
                  {esAdminGerencia && (
                    <td className="px-4 py-3 text-right">
                      <span className={`font-semibold ${vendedor.valor_total_vendido > 0 ? 'text-emerald-400' : 'text-zinc-500'}`}>
                        {formatCurrency(vendedor.valor_total_vendido)}
                      </span>
                    </td>
                  )}
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      vendedor.conversion_cotizacion >= 50 ? 'bg-emerald-500/20 text-emerald-400' :
                      vendedor.conversion_cotizacion >= 25 ? 'bg-amber-500/20 text-amber-400' :
                      'bg-zinc-700 text-zinc-400'
                    }`}>
                      {vendedor.conversion_cotizacion}%
                    </span>
                  </td>
                  {vistaExpandida && (
                    <>
                      <td className="px-4 py-3 text-center">
                        <span className="text-zinc-300">{vendedor.clientes_con_venta}/{vendedor.clientes_con_ficha}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs ${vendedor.conversion_ficha > 0 ? 'text-[#D4AF37]' : 'text-zinc-500'}`}>
                          {vendedor.conversion_ficha}%
                        </span>
                      </td>
                      {esAdminGerencia && (
                        <td className="px-4 py-3 text-right">
                          <span className="text-zinc-300 text-xs">{formatCurrency(vendedor.ticket_promedio)}</span>
                        </td>
                      )}
                    </>
                  )}
                  <td className="px-4 py-3 text-center">
                    {getVariacionIcon(vendedor.variacion)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Gráfica comparativa - Solo si no es soloRanking */}
      {!soloRanking && chartData.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <h4 className="font-medium text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#D4AF37]" />
            Comparativa: Cotizaciones vs Ventas
          </h4>
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis 
                  type="number" 
                  tick={{ fill: '#71717a', fontSize: 11 }}
                />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  tick={{ fill: '#a1a1aa', fontSize: 12 }}
                  width={80}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#18181b',
                    border: '1px solid #27272a',
                    borderRadius: '8px'
                  }}
                  labelStyle={{ color: '#fff' }}
                />
                <Legend />
                <Bar dataKey="cotizaciones" name="Cotizaciones" fill="#6B7280" radius={[0, 4, 4, 0]} />
                <Bar dataKey="ventas" name="Ventas" fill="#10B981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricasVendedores;
